/**
 * Capacitor push notification registration (OneSignal).
 *
 * Uses the official `@onesignal/capacitor-plugin`, whose JS calls route through
 * the `window.Capacitor` bridge — so they work from the remote-webview origin (the
 * native shell loads app.turnkey.coach, not a local bundle), the same way
 * @capacitor/clipboard and @capacitor/browser do. This replaced an earlier
 * `window.OneSignal` cordova-global approach that was not reliable from a remote
 * origin.
 *
 * Push is linked to the signed-in user by setting the OneSignal external id to the
 * Rails user id — exactly how the backend targets pushes (`OneSignal::Sender`), so
 * no backend change is needed. All calls no-op outside the Capacitor shell.
 *
 * @onesignal/capacitor-plugin, @capawesome/capacitor-badge, and @capacitor/app
 * are loaded lazily (dynamic import) so they are excluded from the main entry
 * bundle and never downloaded/parsed in MobiLoud or plain web — only fetched when
 * isCapacitorEnvironment() is true.
 */

import Honeybadger from "@honeybadger-io/js";
import { isCapacitorEnvironment, capacitorPlatform } from "./detection";
import { navigateToDeepLink } from "./deep_links";

// OneSignal App ID is public (the REST key stays server-side in st). This is the
// production TurnKey Coach OneSignal app. If demo ever needs its own OneSignal
// app, serve this from /api/v1/me/app_config instead of hardcoding.
const ONESIGNAL_APP_ID = "28701f68-a571-4857-917a-beb1e6871795";
const BADGE_SYNC_ENDPOINT = "/api/v1/me/badge/sync";

// OSNotificationPermission values from @onesignal/capacitor-plugin (numeric).
// NOTE: only iOS reports the full enum. On Android permissionNative() returns
// ONLY DENIED (1) or AUTHORIZED (2) — it maps a boolean grant state, so a
// never-requested permission reads as DENIED, never NOT_DETERMINED. See
// promptForPushPermission for why that distinction matters.
const OS_PERMISSION_NOT_DETERMINED = 0;
const OS_PERMISSION_DENIED = 1;
const OS_PERMISSION_AUTHORIZED = 2;

// In the remote-webview shell the native OneSignal/APNs setup can lag the JS
// initialize() promise, so an immediate requestPermission() never presents the
// iOS prompt (device shows "Never Prompted"). We settle, then retry while the
// native permission is still undetermined.
const PERMISSION_SETTLE_MS = 1500;
const PERMISSION_RETRY_MS = 1500;
const PERMISSION_MAX_ATTEMPTS = 5;

// On resume, wait for OneSignal's clear-on-open before re-setting the badge.
const BADGE_RESUME_DELAY_MS = 1000;

function delay(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// Cached import promises — resolved once, reused thereafter. Cleared if the
// module-level state is reset (jest.resetModules() effectively clears them since
// this module itself is re-required fresh each time).
let _oneSignalImport = null;
let _badgeImport = null;
let _appImport = null;

function getOneSignal() {
  if (!_oneSignalImport) {
    _oneSignalImport = import("@onesignal/capacitor-plugin")
      .then((mod) => mod.default)
      .catch((error) => {
        _oneSignalImport = null; // allow retry on next call
        throw error;
      });
  }
  return _oneSignalImport;
}

function getBadge() {
  if (!_badgeImport) {
    _badgeImport = import("@capawesome/capacitor-badge")
      .then(({ Badge }) => Badge)
      .catch((error) => {
        _badgeImport = null; // allow retry on next call
        throw error;
      });
  }
  return _badgeImport;
}

function getApp() {
  if (!_appImport) {
    _appImport = import("@capacitor/app")
      .then(({ App }) => App)
      .catch((error) => {
        _appImport = null; // allow retry on next call
        throw error;
      });
  }
  return _appImport;
}

// Memoizes OneSignal.initialize() alone (resolves to whether it succeeded).
// Cleared on failure so a later call can retry. Kept separate from the full
// setup flow so registerCapacitorDevice can await initialization WITHOUT waiting
// on the (potentially long) permission prompt before linking the user.
let initializePromise = null;
// Memoizes the full init flow (initialize + permission + link + badge) so
// initCapacitorPush runs at most once on success.
let setupPromise = null;

/**
 * Whether a user is authenticated. Rails sets window._tkcCurrentUserId in the
 * layout for authenticated requests (shared with the MobiLoud path).
 * @returns {boolean}
 */
export function isUserAuthenticated() {
  return typeof window !== "undefined" && !!window._tkcCurrentUserId;
}

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute("content") : null;
}

// TEMPORARY push diagnostics. Gated on the `capacitor_push_debug` feature flag
// (enable per-user for QA) OR a localStorage `tkc_push_debug=1` escape hatch a
// tester can set via chrome://inspect. Logs the Android permission flow to the
// console so a device logcat / remote-inspect trace shows exactly where the
// "Allow notifications?" prompt fails to present. No-op when neither is set.
// Remove once the Android prompt is resolved on device.
function pushDebugEnabled() {
  try {
    if (
      typeof window !== "undefined" &&
      Array.isArray(window._tkcEnabledFeatures) &&
      window._tkcEnabledFeatures.includes("capacitor_push_debug")
    ) {
      return true;
    }
    if (
      typeof localStorage !== "undefined" &&
      localStorage.getItem("tkc_push_debug") === "1"
    ) {
      return true;
    }
  } catch {
    // reading storage / globals must never break push setup
  }
  return false;
}

// Accumulated diagnostic lines, also mirrored into an on-screen overlay so a
// tester with no adb / chrome://inspect can just screenshot the phone.
const _pushDebugLines = [];

function renderPushDebugOverlay() {
  try {
    if (typeof document === "undefined" || !document.body) {
      return;
    }
    let el = document.getElementById("__tkc_push_dbg");
    if (!el) {
      el = document.createElement("div");
      el.id = "__tkc_push_dbg";
      el.style.cssText =
        "position:fixed;left:0;right:0;bottom:0;z-index:2147483647;" +
        "max-height:45vh;overflow:auto;background:#0b3d2e;color:#7cfc00;" +
        "font:10px/1.35 monospace;padding:6px 8px;white-space:pre-wrap;" +
        "word-break:break-word;";
      document.body.appendChild(el);
    }
    el.textContent =
      "TKC PUSH DEBUG — please screenshot this\n" + _pushDebugLines.join("\n");
  } catch {
    // the overlay is best-effort and must never break push setup
  }
}

function pushLog(...args) {
  if (!pushDebugEnabled()) {
    return;
  }
  const line = args
    .map((a) => (typeof a === "string" ? a : JSON.stringify(a)))
    .join(" ");
  _pushDebugLines.push(line);
  if (typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.log("[TKCpush]", line);
  }
  renderPushDebugOverlay();
}

/**
 * Report a push-setup failure so it's visible in monitoring. Reporting must
 * never itself break the page.
 */
function reportPushError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_push_init" },
    });
  } catch {
    // no-op
  }
}

/**
 * Link this device to the current user in OneSignal via external id. Awaits
 * login so success is only reported once the link resolves, and swallows
 * rejections so they never surface as unhandled promise rejections.
 * @returns {Promise<boolean>} whether the device was linked
 */
async function linkCurrentUser() {
  if (!isUserAuthenticated()) {
    return false;
  }
  try {
    const OneSignal = await getOneSignal();
    await OneSignal.login(String(window._tkcCurrentUserId));
    return true;
  } catch {
    return false;
  }
}

/**
 * Set the app icon badge to `count` (clear it when 0). No-op outside the
 * Capacitor shell and on non-iOS platforms (Badge plugin is iOS-only);
 * never throws. We set the badge directly rather than relying on OneSignal,
 * which clears the badge on app open and whose silent-push re-sync is
 * unreliable on iOS.
 */
async function setNativeBadge(count) {
  if (!isCapacitorEnvironment()) {
    return;
  }
  // The @capawesome/capacitor-badge plugin is iOS-only. Calling it on Android
  // throws "Badge plugin is not implemented on android". Guard here so we never
  // reach the dynamic import on any non-iOS platform.
  if (capacitorPlatform() !== "ios") {
    return;
  }
  try {
    const Badge = await getBadge();
    if (count > 0) {
      await Badge.set({ count });
    } else {
      await Badge.clear();
    }
  } catch (error) {
    reportPushError(error);
  }
}

// Monotonic id so overlapping syncs (rapid resumes) can't let an older response
// overwrite the badge with a stale count — only the latest sync applies.
let badgeSyncSeq = 0;

/**
 * Sync the app icon badge to the current unread count (on launch and app
 * resume). POSTs to the badge endpoint (which computes the authoritative count)
 * and sets the native badge from the response — but only if no newer sync has
 * started in the meantime. Best-effort.
 */
async function syncBadge() {
  if (!isUserAuthenticated()) {
    return;
  }
  const csrfToken = getCsrfToken();
  if (!csrfToken) {
    return;
  }
  badgeSyncSeq += 1;
  const seq = badgeSyncSeq;
  try {
    const response = await fetch(BADGE_SYNC_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
    });
    if (response.ok) {
      const data = await response.json();
      // A newer sync superseded this one — drop this (possibly stale) result.
      if (seq === badgeSyncSeq) {
        await setNativeBadge(data.badge_count || 0);
      }
    }
  } catch {
    // Badge sync is best-effort.
  }
}

let resumeListenerRegistered = false;

/**
 * Re-sync the badge whenever the app returns to the foreground. OneSignal clears
 * the badge on app open, so we wait briefly to let that happen, then set the
 * badge back to the authoritative count. Registered once.
 */
function registerBadgeResumeSync() {
  if (resumeListenerRegistered) {
    return;
  }
  resumeListenerRegistered = true;
  getApp()
    .then((App) => {
      try {
        App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) {
            delay(BADGE_RESUME_DELAY_MS).then(() => syncBadge());
          }
        });
      } catch (error) {
        reportPushError(error);
      }
    })
    .catch(reportPushError);
}

/**
 * Request notification permission robustly. Settles, then while the native
 * permission is still undetermined, calls requestPermission and retries — this
 * covers the remote-webview lag where the prompt silently fails to present if
 * requested too early. Never throws.
 * @returns {Promise<boolean>} whether permission is granted
 */
async function promptForPushPermission() {
  const OneSignal = await getOneSignal();
  await delay(PERMISSION_SETTLE_MS);

  // Android's permissionNative() never reports NOT_DETERMINED — a never-requested
  // permission reads as DENIED (1) (it maps the boolean grant state; see
  // @onesignal/capacitor-plugin). So the iOS "already decided, skip requesting"
  // short-circuit below would treat a first-launch user as permanently denied and
  // NEVER present the prompt (device stays "Permission Not Granted", never
  // subscribes). Handle Android explicitly with the SDK's canRequestPermission
  // (true only while the OS would still show a prompt): request when it can still
  // present, so a user who already declined isn't re-prompted on every cold start
  // (initCapacitorPush runs each launch). fallbackToSettings=false so a launch
  // never yanks a declined user into system settings.
  //
  // canRequestPermission() has TWO false meanings: the user genuinely declined, OR
  // the native OneSignal SDK simply hasn't finished initializing yet in the
  // remote-webview shell (the same lag the iOS path retries for — initialize()'s
  // promise can resolve before the native permission subsystem is ready). Bailing
  // on the first false read is why fresh Android installs saw no prompt at all.
  // So we retry the readiness gate: once canRequestPermission() is true the SDK is
  // ready and requestPermission() will present, so we request once and return the
  // user's decision. If it stays false through every attempt, it's a real prior
  // decline and we give up without prompting.
  const isAndroid = capacitorPlatform() === "android";
  if (isAndroid) {
    pushLog("android permission flow start");
    for (let attempt = 0; attempt < PERMISSION_MAX_ATTEMPTS; attempt += 1) {
      try {
        const current = await OneSignal.Notifications.permissionNative();
        const canRequest = await OneSignal.Notifications.canRequestPermission();
        pushLog(
          "android attempt",
          attempt,
          "permissionNative =",
          current,
          "canRequestPermission =",
          canRequest,
        );
        if (current === OS_PERMISSION_AUTHORIZED) {
          return true; // already granted (e.g. migrated from the MobiLoud app)
        }
        if (canRequest) {
          pushLog("android requesting permission");
          const requested =
            await OneSignal.Notifications.requestPermission(false);
          const decided = await OneSignal.Notifications.permissionNative();
          pushLog(
            "android requestPermission returned",
            requested,
            "permissionNative now",
            decided,
          );
          return decided === OS_PERMISSION_AUTHORIZED;
        }
        // canRequestPermission() false: either a real prior decline or the SDK is
        // still settling. Wait and re-check rather than giving up on attempt one.
      } catch (error) {
        pushLog(
          "android error:",
          String(error && error.message ? error.message : error),
        );
        reportPushError(error);
      }
      await delay(PERMISSION_RETRY_MS);
    }
    // Still not requestable after every retry — treat as a prior decline (the OS
    // won't present the prompt), no report: this is an expected steady state.
    pushLog(
      "android gate never became requestable after",
      PERMISSION_MAX_ATTEMPTS,
      "attempts — giving up without prompting",
    );
    return false;
  }

  for (let attempt = 0; attempt < PERMISSION_MAX_ATTEMPTS; attempt += 1) {
    try {
      const permission = await OneSignal.Notifications.permissionNative();
      if (permission !== OS_PERMISSION_NOT_DETERMINED) {
        // Already decided (granted / denied / provisional / ephemeral).
        return permission !== OS_PERMISSION_DENIED;
      }
      await OneSignal.Notifications.requestPermission(true);
      const decided = await OneSignal.Notifications.permissionNative();
      if (decided !== OS_PERMISSION_NOT_DETERMINED) {
        return decided !== OS_PERMISSION_DENIED;
      }
      // Still undetermined — the prompt didn't present yet (SDK not ready).
    } catch (error) {
      reportPushError(error);
    }
    await delay(PERMISSION_RETRY_MS);
  }
  // The prompt never presented — surface it so it's visible in monitoring.
  reportPushError(
    new Error(
      "Capacitor push: permission prompt never presented after retries",
    ),
  );
  return false;
}

let clickListenerRegistered = false;

/**
 * Extract the in-app destination from a OneSignal notification-click event.
 *
 * `launchURL` / `result.url` come first: that is the top-level url the server
 * computed for the WEB shells (PushNotifications::Dispatcher#launch_url), and it
 * is the only one guaranteed to be a routable web path — for a push paired with
 * an AppNotification it is the canonical destination that email and the in-app
 * flyout also use.
 *
 * `additionalData.deep_link` is the last resort, because it is the NATIVE mobile
 * route: the Flutter app's router consumes it verbatim, and its "/conversations/<id>"
 * and plural "/workouts/<id>" shapes have no Rails web route. Preferring it (as
 * this did originally) sent the web view to the 404 catch-all on every chat and
 * workout push. resolveDeepLinkPath rewrites those shapes, so it is safe as a
 * fallback for a push that carried no top-level url at all.
 */
function deepLinkTargetFromClick(event) {
  const notification = event?.notification;
  return (
    notification?.launchURL ||
    event?.result?.url ||
    notification?.additionalData?.deep_link ||
    null
  );
}

/**
 * Route a tapped push to its destination inside the web view.
 *
 * Without this the tap does nothing beyond foregrounding the app: the OneSignal
 * SDK's own launch-url handling opens the url in an in-app browser view on iOS,
 * which does NOT re-enter the app through Universal Links, so `appUrlOpen`
 * (deep_links.js) never fires. Handling the click directly makes the tap behave
 * identically on iOS and Android. Registered once, right after initialize() so a
 * cold start from a tap isn't waiting on the permission prompt. Never throws.
 */
function registerNotificationClicks(OneSignal) {
  if (clickListenerRegistered) {
    return;
  }
  clickListenerRegistered = true;
  try {
    OneSignal.Notifications.addEventListener("click", (event) => {
      try {
        navigateToDeepLink(deepLinkTargetFromClick(event));
      } catch (error) {
        reportPushError(error);
      }
    });
  } catch (error) {
    clickListenerRegistered = false; // allow a later call to retry
    reportPushError(error);
  }
}

/**
 * Ensure OneSignal.initialize() has run (memoized). Resolves to whether init
 * succeeded. On failure it reports and clears the memo so a later call retries —
 * a transient failure must not silently disable push for the whole session.
 * @returns {Promise<boolean>}
 */
function ensureInitialized() {
  if (initializePromise) {
    return initializePromise;
  }
  initializePromise = getOneSignal()
    .then(async (OneSignal) => {
      // `await` (rather than .then) so a non-thenable return from initialize()
      // still sequences correctly.
      await OneSignal.initialize(ONESIGNAL_APP_ID);
      if (pushDebugEnabled()) {
        try {
          // 6 = Verbose in OneSignal's LogLevel enum. Surfaces the native
          // subscription/permission flow in logcat next to our [TKCpush] logs.
          OneSignal.Debug.setLogLevel(6);
        } catch (error) {
          pushLog("setLogLevel failed:", String(error));
        }
        pushLog("initialized; platform =", capacitorPlatform());
      }
      // Register the click handler as part of initialization (not in the
      // initCapacitorPush flow) so every entry point gets it, and so it is in
      // place before the permission prompt's retry loop can delay it — a cold
      // start FROM a notification tap delivers the click event right after init.
      registerNotificationClicks(OneSignal);
      return true;
    })
    .catch((error) => {
      initializePromise = null; // allow a later call to retry
      reportPushError(error);
      return false;
    });
  return initializePromise;
}

/**
 * Initialize OneSignal and, if signed in, link the current user. Safe to call
 * multiple times and in any environment — it no-ops outside the Capacitor shell
 * and never throws (so it can be fired without awaiting on page load). Runs at
 * most once on success; retries the whole flow if initialization failed.
 * @returns {Promise<void>}
 */
export function initCapacitorPush() {
  if (!isCapacitorEnvironment()) {
    return Promise.resolve();
  }
  if (setupPromise) {
    return setupPromise;
  }

  registerBadgeResumeSync();

  setupPromise = (async () => {
    pushLog(
      "setup start; platform =",
      capacitorPlatform(),
      "authed =",
      isUserAuthenticated(),
    );
    if (!(await ensureInitialized())) {
      pushLog("ensureInitialized FAILED (see Honeybadger); aborting setup");
      setupPromise = null; // allow a later call to retry the whole flow
      return;
    }
    // Link + badge first so they aren't held up by the permission retry loop.
    if (isUserAuthenticated()) {
      await linkCurrentUser();
    }
    await syncBadge();
    // Then request notification permission (settles + retries; never throws).
    await promptForPushPermission();
  })();

  return setupPromise;
}

/**
 * Link the current device to the signed-in user (call after login). Awaits
 * OneSignal initialization first (memoized, so it's cheap if already done) but
 * NOT the permission prompt, so a user who signs in right after launch is linked
 * as soon as init completes rather than failing because init was still in-flight.
 * @returns {Promise<boolean>} whether the external id was set
 */
export async function registerCapacitorDevice() {
  if (!isCapacitorEnvironment()) {
    return false;
  }
  if (!(await ensureInitialized())) {
    return false;
  }
  return linkCurrentUser();
}

/**
 * Dissociate this device from the user in OneSignal (call on logout).
 */
export function clearCapacitorDevice() {
  if (!isCapacitorEnvironment()) {
    return;
  }
  // logout() is async; swallow its rejection so sign-out never surfaces an
  // unhandled rejection.
  getOneSignal()
    .then((OneSignal) => OneSignal.logout())
    .catch(() => {});
}

export default {
  isUserAuthenticated,
  initCapacitorPush,
  registerCapacitorDevice,
  clearCapacitorDevice,
};
